// src/presentation/pages/ClinicalExamination/sections/VisionSection.tsx
import React from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import EyeIcon from "@/assets/visibility.svg?react";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "react-i18next";
import { SevereVisionWarning } from "./SevereVisionWarning";

interface VisionSectionProps {
  visionStatus: string;
  visionAssistive: {
    wearsGlasses: boolean;
    usesContacts: boolean;
    sensitiveToLight: boolean;
  };
  visualAidPurpose: {
    forReading: boolean;
    forDistance: boolean;
    other: boolean;
  };
  visualAidOtherText: string;
  onVisionStatusChange: (value: string) => void;
  onVisionAssistiveChange: (
    field: keyof VisionSectionProps["visionAssistive"],
    checked: boolean,
  ) => void;
  onVisualAidPurposeChange: (
    field: keyof VisionSectionProps["visualAidPurpose"],
    checked: boolean,
  ) => void;
  onVisualAidOtherTextChange: (value: string) => void;
}

export const VisionSection: React.FC<VisionSectionProps> = ({
  visionStatus,
  visionAssistive,
  visualAidPurpose,
  visualAidOtherText,
  onVisionStatusChange,
  onVisionAssistiveChange,
  onVisualAidPurposeChange,
  onVisualAidOtherTextChange,
}) => {
  const { t } = useTranslation("patients");
  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <EyeIcon />
        <h4 className="text-base font-semibold text-gray-800 border-b">
          {t("clinicalSections.vision.title")}
        </h4>
      </div>

      {/* Vision Status */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-900">
          {t("clinicalSections.vision.statusLabel")}
        </p>
        <RadioGroup
          value={visionStatus}
          onValueChange={onVisionStatusChange}
          className="space-y-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="normal" id="vision-normal" />
            <Label
              htmlFor="vision-normal"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.normal")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="mild" id="vision-mild" />
            <Label
              htmlFor="vision-mild"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.mild")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="severe" id="vision-severe" />
            <Label
              htmlFor="vision-severe"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.severe")}
            </Label>
          </div>
        </RadioGroup>
        {visionStatus === "severe" && <SevereVisionWarning />}
      </div>

      {/* Assistive/Functional */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-900">
          {t("clinicalSections.vision.assistiveFunctional")}
        </p>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="wears-glasses"
              checked={visionAssistive.wearsGlasses}
              onCheckedChange={(checked) =>
                onVisionAssistiveChange("wearsGlasses", checked as boolean)
              }
            />
            <Label
              htmlFor="wears-glasses"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.wearsGlasses")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="uses-contacts"
              checked={visionAssistive.usesContacts}
              onCheckedChange={(checked) =>
                onVisionAssistiveChange("usesContacts", checked as boolean)
              }
            />
            <Label
              htmlFor="uses-contacts"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.usesContacts")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sensitive-light"
              checked={visionAssistive.sensitiveToLight}
              onCheckedChange={(checked) =>
                onVisionAssistiveChange("sensitiveToLight", checked as boolean)
              }
            />
            <Label
              htmlFor="sensitive-light"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.sensitiveToLight")}
            </Label>
          </div>
        </div>
      </div>

      {/* Purpose of Visual Aid */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-900">
          {t("clinicalSections.vision.purposeOfVisualAid")}
        </p>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="for-reading"
              checked={visualAidPurpose.forReading}
              onCheckedChange={(checked) =>
                onVisualAidPurposeChange("forReading", checked as boolean)
              }
            />
            <Label
              htmlFor="for-reading"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.forReading")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="for-distance"
              checked={visualAidPurpose.forDistance}
              onCheckedChange={(checked) =>
                onVisualAidPurposeChange("forDistance", checked as boolean)
              }
            />
            <Label
              htmlFor="for-distance"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.forDistance")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="other-purpose"
              checked={visualAidPurpose.other}
              onCheckedChange={(checked) =>
                onVisualAidPurposeChange("other", checked as boolean)
              }
            />
            <Label
              htmlFor="other-purpose"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.vision.other")}
            </Label>
          </div>
          {visualAidPurpose.other && (
            <Textarea
              placeholder={t("clinicalSections.vision.pleaseSpecify")}
              value={visualAidOtherText}
              onChange={(e) => onVisualAidOtherTextChange(e.target.value)}
              className="mt-2"
            />
          )}
        </div>
      </div>
    </div>
  );
};
